import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertTriangle,
  ArrowUpRight,
  Check,
  Clock,
  Copy,
  FileText,
  Upload as UploadIcon,
  X as XIcon,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Spinner } from '@/components/ui/spinner';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { PartyChoice } from '@/components/documents/party-choice';
import {
  MAX_UPLOAD_SIZE_LABEL,
  getDocumentStatus,
  uploadFileChunked,
  type ClassificationItem,
  type DocumentParty,
  type ExistingDocument,
} from '@/lib/api/documents';
import { ApiError } from '@/lib/api/client';
import { documentClassLabel } from '@/lib/document-classes';
import { formatDateTime } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';

/**
 * T-0113 / E02-F01 — Upload pipeline modal.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-round3.js`
 * → `Modals.openUpload`. Drop zone + per-file progress + classification
 * chip + AI confidence chip + cancel / retry affordances.
 *
 * Behavior contract (T-0113 §1 + T-0040 chunked upload, fixed in T-0160):
 *   - "Whose document is this?" (E19 / T-0215) sits above the drop zone:
 *     Vendor (the default) · Avero · Client, asked once per batch. The
 *     choice is sent as `source_party` on every initiate; the modal owns
 *     it, callers pass nothing. (Every upload used to be tagged Avero
 *     silently.)
 *   - Drag-drop a file (or click to browse) → adds a row.
 *   - Each row runs `uploadFileChunked` — the one upload implementation the
 *     wizard and the image slot use too: initiate → EVERY chunk → complete.
 *     Its byte progress drives the bar (bytes the server received / total;
 *     nothing about it is fabricated — R6). It skips the chunks only when
 *     the server says `deduped: true`; the row then says the file is
 *     already in this engagement and names the copy (title, upload date).
 *     (This modal used to read any `document_id` from initiate as "already
 *     uploaded" — initiate returns one for every upload — and sent no bytes.)
 *   - After `complete`, we poll `GET /documents/:id/status`:
 *       parse_status `parsed` → "Parsed";
 *       `failed` → the failure, with the reader's reason;
 *       still running when the poll window ends → a neutral "Still
 *       processing — it will appear in the Documents list when ready".
 *     Never "Parsed" for a file that was not read.
 *   - Per-row affordances: cancel (in flight or queued), retry (an upload
 *     that errored), remove from list.
 *
 * Stay-out zones (T-0113 prompt):
 *   - We do NOT navigate to /documents/review here — that's the route
 *     the docs library already links to. We just close cleanly.
 *   - A failed read shows its reason here; retry / delete for it live on
 *     the Documents screen row (and the parse-tree modal, T-0114).
 *
 * Dismiss rules:
 *   - ESC closes the modal (Radix Dialog).
 *   - Click-outside dismisses ONLY when no upload is in flight (the
 *     `onPointerDownOutside` interceptor). Mid-upload dismiss is
 *     suppressed so the user doesn't accidentally throw away a
 *     half-pushed file.
 *   - Explicit Close button always works (the user can choose to
 *     abandon).
 */

type RowState =
  | { kind: 'queued' }
  | { kind: 'uploading'; pushedBytes: number; totalBytes: number }
  | { kind: 'parsing'; documentId: string }
  | {
      kind: 'parsed';
      documentId: string;
      classification: ClassificationItem | null;
    }
  /** The read failed; `reason` is the reader's, when it gave one. */
  | { kind: 'failed'; documentId: string; reason: string | null }
  /** Stored, but not read within the poll window — not an error, not a success. */
  | { kind: 'processing'; documentId: string }
  /** The engagement already holds a usable copy; nothing was uploaded. */
  | { kind: 'duplicate'; documentId: string; existing: ExistingDocument | null }
  /** The upload itself failed (initiate / chunk / complete). */
  | { kind: 'error'; message: string };

type RowKind = RowState['kind'];

interface FileRow {
  id: string;
  file: File;
  state: RowState;
  /** Abort controller used to cancel in-flight chunk pushes. */
  abort: AbortController | null;
}

export interface UploadModalProps {
  engagementId: string;
  /** Project id needed by `POST /api/v1/documents`. */
  projectId: string;
  /** Open / close — driven by the modal context. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * How the parse-status poll runs after a file is stored. Defaults to
   * every 2s for up to 90 polls (3 minutes); tests shorten it.
   */
  statusPoll?: { intervalMs?: number; maxAttempts?: number };
}

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_POLL_ATTEMPTS = 90;

export const STILL_PROCESSING_MESSAGE =
  'Still processing — it will appear in the Documents list when ready';

const IN_FLIGHT: ReadonlySet<RowKind> = new Set<RowKind>(['queued', 'uploading', 'parsing']);

/**
 * What the file picker offers: the API's general allowlist (PDF, Word,
 * Excel, plain text — T-0228 added TXT, which the native parser reads as
 * one page; T-0252 added PowerPoint and email, which it reads slide by
 * slide and as headers, body and attachment list).
 */
export const UPLOAD_ACCEPT = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.pptx',
  '.txt',
  '.eml',
  '.msg',
  '.png',
  '.jpg',
  '.jpeg',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'message/rfc822',
  'application/vnd.ms-outlook',
  'image/png',
  'image/jpeg',
].join(',');

export function UploadModal({
  engagementId,
  projectId,
  open,
  onOpenChange,
  statusPoll,
}: UploadModalProps) {
  const qc = useQueryClient();
  const titleId = useId();
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollIntervalMs = statusPoll?.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const pollAttempts = statusPoll?.maxAttempts ?? DEFAULT_POLL_ATTEMPTS;
  // Whose document it is — one answer for the batch, Vendor until changed.
  const [party, setParty] = useState<DocumentParty>('vendor');

  // Mutable rows table. We mutate via `setRows([...rows])` after every
  // chunk so the UI gets a fresh tick. The Map keeps lookups cheap and
  // the per-row generation counter (`abort`) keeps cancellation safe.
  const [rows, setRows] = useState<FileRow[]>([]);
  const rowsRef = useRef<FileRow[]>(rows);
  rowsRef.current = rows;
  const [drop, setDrop] = useState<'idle' | 'over'>('idle');
  const depthRef = useRef(0);

  // Stop every in-flight upload + parse-poll when the modal is closed.
  useEffect(() => {
    if (!open) {
      for (const r of rowsRef.current) {
        r.abort?.abort();
      }
      // Reset the rows so a re-open starts with a blank list. The
      // engagement documents list is what carries already-uploaded docs
      // back to the library, so nothing is lost.
      setRows([]);
    }
  }, [open]);

  const anyInFlight = useMemo(() => rows.some((r) => IN_FLIGHT.has(r.state.kind)), [rows]);
  const counts = useMemo(() => {
    const c: Record<RowKind, number> = {
      queued: 0,
      uploading: 0,
      parsing: 0,
      parsed: 0,
      failed: 0,
      processing: 0,
      duplicate: 0,
      error: 0,
    };
    for (const r of rows) c[r.state.kind] += 1;
    return c;
  }, [rows]);
  // "N of M ready for review": M counts what reached the server. A file the
  // API refused (an unsupported type, a failed chunk) is not among them
  // (T-0228, C2-17).
  const uploadedCount = rows.length - counts.error;

  // The Documents screen lists the engagement documents (T-0160) and
  // enriches them with classifications; after a stored upload we refresh
  // both so the new document surfaces in the grid behind the modal.
  const invalidateLibrary = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['documents', 'classifications', engagementId] });
    void qc.invalidateQueries({ queryKey: ['engagement-documents'] });
  }, [qc, engagementId]);

  const setRowState = useCallback((rowId: string, next: RowState) => {
    setRows((cur) => cur.map((r) => (r.id === rowId ? { ...r, state: next } : r)));
  }, []);

  // Drive one row through the chunked upload + status poll. Cancellation
  // is propagated via the row's AbortController.
  const driveUpload = useCallback(
    async (row: FileRow) => {
      const signal = row.abort?.signal;
      setRowState(row.id, { kind: 'uploading', pushedBytes: 0, totalBytes: row.file.size });

      let result: Awaited<ReturnType<typeof uploadFileChunked>>;
      try {
        result = await uploadFileChunked(row.file, {
          projectId,
          title: row.file.name,
          sourceParty: party,
          signal,
          onProgress: ({ sentBytes, totalBytes }) => {
            if (signal?.aborted) return;
            setRowState(row.id, { kind: 'uploading', pushedBytes: sentBytes, totalBytes });
          },
        });
      } catch (err) {
        if (signal?.aborted) return;
        setRowState(row.id, { kind: 'error', message: humanizeError(err) });
        return;
      }
      if (signal?.aborted) return;
      invalidateLibrary();

      if (result.deduped) {
        setRowState(row.id, {
          kind: 'duplicate',
          documentId: result.document_id,
          existing: result.existing_document ?? null,
        });
        return;
      }
      await pollParseStatus(row.id, result.document_id, setRowState, signal, qc, {
        intervalMs: pollIntervalMs,
        maxAttempts: pollAttempts,
      });
    },
    [projectId, party, invalidateLibrary, qc, setRowState, pollIntervalMs, pollAttempts],
  );

  const ingestFiles = useCallback(
    (files: FileList | File[] | null | undefined) => {
      if (!files || (files instanceof FileList && files.length === 0)) return;
      const list = Array.from(files instanceof FileList ? files : files);
      const next: FileRow[] = list.map((f) => ({
        id: cryptoRandomId(),
        file: f,
        state: { kind: 'queued' },
        abort: new AbortController(),
      }));
      setRows((cur) => [...cur, ...next]);
      // Fire each upload — they run in parallel. The fetch layer's
      // limits keep the browser sane.
      for (const r of next) void driveUpload(r);
    },
    [driveUpload],
  );

  function onPickClick() {
    fileInputRef.current?.click();
  }

  function onCancelRow(rowId: string) {
    const row = rowsRef.current.find((r) => r.id === rowId);
    if (!row) return;
    row.abort?.abort();
    setRows((cur) => cur.filter((r) => r.id !== rowId));
  }
  function onRetryRow(rowId: string) {
    const row = rowsRef.current.find((r) => r.id === rowId);
    if (!row) return;
    const fresh: FileRow = { ...row, state: { kind: 'queued' }, abort: new AbortController() };
    setRows((cur) => cur.map((r) => (r.id === rowId ? fresh : r)));
    void driveUpload(fresh);
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          for (const r of rowsRef.current) r.abort?.abort();
        }
        onOpenChange(o);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          aria-describedby={undefined}
          onPointerDownOutside={(e) => {
            // Suppress click-outside dismiss mid-upload (acceptance §scope).
            // The explicit Close button still works.
            if (anyInFlight) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (anyInFlight) e.preventDefault();
          }}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(880px,94vw)] max-h-[88vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          {/* Header */}
          <header className="flex items-start justify-between gap-3 border-b border-ink-100 px-6 py-4 pr-14">
            <div className="min-w-0">
              <div className="text-meta uppercase tracking-wide text-ink-500">Documents</div>
              <Dialog.Title id={titleId} className="text-h2 font-semibold text-ink-900">
                Upload baseline & engagement documents
              </Dialog.Title>
              <p className="mt-1 text-meta text-ink-500">
                Chunked upload · failed uploads can be retried · routed by AI on parse
              </p>
            </div>
            <Dialog.Close
              aria-label="Close upload modal"
              className={cn(
                'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
                'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
              )}
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </header>

          {/* Body */}
          <div className="overflow-y-auto px-6 py-5">
            {/* Asked before the files, since an upload starts the moment a
                file lands: the answer goes on every initiate in the batch. */}
            <PartyChoice value={party} onChange={setParty} className="mb-4" />
            <div
              role="button"
              tabIndex={0}
              aria-label="Drop files here or click to browse"
              data-state={drop}
              onDragEnter={(e) => {
                e.preventDefault();
                depthRef.current += 1;
                setDrop('over');
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                depthRef.current -= 1;
                if (depthRef.current <= 0) {
                  depthRef.current = 0;
                  setDrop('idle');
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                depthRef.current = 0;
                setDrop('idle');
                ingestFiles(e.dataTransfer?.files);
              }}
              onClick={onPickClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPickClick();
                }
              }}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-large border border-dashed border-ink-200 bg-paper-50 px-6 py-10 text-center',
                'transition-colors hover:border-ink-300',
                drop === 'over' && 'border-accent-amber-600 bg-accent-amber-50',
              )}
            >
              <UploadIcon className="h-6 w-6 text-ink-500" aria-hidden="true" />
              <div className="text-body font-semibold text-ink-900">
                Drop files here or click to browse
              </div>
              <div className="text-meta text-ink-500">
                PDF · DOCX · XLSX · PPTX · TXT · EML · MSG — up to {MAX_UPLOAD_SIZE_LABEL} per file
              </div>
              <div className="mt-1 flex flex-wrap items-center justify-center gap-1.5">
                {/* Not "resume on drop": a failed chunk fails the row, and Retry sends
                    the file again from the first chunk. */}
                <PassiveChip>Chunked at 1 MB</PassiveChip>
                {/* Not "Server-side virus scan": nothing scans uploads for malware yet
                    (ADR-019 plans Defender for Storage for vendor files). */}
                <PassiveChip>Stored privately</PassiveChip>
                <PassiveChip>Routed by AI on parse</PassiveChip>
              </div>
              <input
                id={inputId}
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={UPLOAD_ACCEPT}
                data-testid="upload-file-input"
                onChange={(e) => {
                  ingestFiles(e.target.files);
                  // Reset so the same file can be re-picked after error.
                  e.target.value = '';
                }}
              />
            </div>

            {rows.length > 0 ? (
              <div className="mt-5">
                <div className="mb-2 flex items-baseline justify-between">
                  <div className="text-h3 font-semibold text-ink-900">
                    Active uploads{' '}
                    <span className="ml-1 text-meta font-normal text-ink-500">
                      {rows.length} file{rows.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {counts.parsed > 0 ? (
                      <StatusBadge tone="healthy">{counts.parsed} parsed</StatusBadge>
                    ) : null}
                    {counts.parsing > 0 ? (
                      <StatusBadge tone="at-risk">{counts.parsing} parsing</StatusBadge>
                    ) : null}
                    {counts.processing > 0 ? (
                      <StatusBadge tone="neutral">{counts.processing} processing</StatusBadge>
                    ) : null}
                    {counts.uploading > 0 ? (
                      <StatusBadge tone="ai">{counts.uploading} uploading</StatusBadge>
                    ) : null}
                    {counts.queued > 0 ? (
                      <StatusBadge tone="neutral">{counts.queued} queued</StatusBadge>
                    ) : null}
                    {counts.duplicate > 0 ? (
                      <StatusBadge tone="neutral">{counts.duplicate} already uploaded</StatusBadge>
                    ) : null}
                    {counts.error + counts.failed > 0 ? (
                      <StatusBadge tone="critical">
                        {counts.error + counts.failed} error
                      </StatusBadge>
                    ) : null}
                  </div>
                </div>
                <ul className="flex flex-col gap-2">
                  {rows.map((r) => (
                    <li key={r.id}>
                      <FileRowView
                        row={r}
                        onCancel={() => onCancelRow(r.id)}
                        onRetry={() => onRetryRow(r.id)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p className="mt-5 rounded-card bg-paper-50 px-4 py-3 text-meta text-ink-500">
              On parse completion, documents are routed by AI to candidate register entries with a
              confidence score and citation anchor. Review proposals on the AI proposal review tab
              before they hit the registers.
            </p>
          </div>

          {/* Footer */}
          <footer className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
            <div className="text-meta text-ink-500">
              {counts.parsed} of {uploadedCount} ready for review · auto-saved
            </div>
            <div className="flex items-center gap-2">
              <Dialog.Close
                className={cn(
                  'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                  'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                )}
              >
                Done
              </Dialog.Close>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Per-row visual. Re-used for every state — the layout doesn't shift
 * across phases, only the chip + progress + meta line change.
 */
function FileRowView({
  row,
  onCancel,
  onRetry,
}: {
  row: FileRow;
  onCancel: () => void;
  onRetry: () => void;
}) {
  const { file, state } = row;
  const percent = (() => {
    if (state.kind === 'uploading') {
      return state.totalBytes === 0 ? 0 : Math.floor((state.pushedBytes / state.totalBytes) * 100);
    }
    if (state.kind === 'queued' || state.kind === 'error') return 0;
    // Every byte is on the server for the rest: parsing, parsed, processing,
    // failed (the read failed, not the upload), duplicate (nothing to send).
    return 100;
  })();
  const classification = state.kind === 'parsed' ? state.classification : null;
  const confidence = classification?.confidence ?? null;
  const note = rowNote(state);

  return (
    <div
      data-testid="upload-row"
      className={cn(
        'grid grid-cols-[40px,1fr,auto,auto] items-center gap-3 rounded-card border border-ink-100 bg-paper-0 px-3 py-2.5',
        (state.kind === 'error' || state.kind === 'failed') && 'border-posture-critical/40',
      )}
    >
      <div
        aria-hidden="true"
        className="flex h-9 w-9 items-center justify-center rounded-card bg-paper-100 text-ink-700"
      >
        <FileText className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="truncate text-body-sm font-medium text-ink-900">{file.name}</div>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-paper-100">
          <span
            className={cn(
              'block h-full',
              state.kind === 'error' || state.kind === 'failed'
                ? 'bg-posture-critical'
                : 'bg-accent-amber-600',
              state.kind === 'parsing' && 'bg-posture-at-risk',
              state.kind === 'parsed' && 'bg-posture-healthy',
              (state.kind === 'processing' || state.kind === 'duplicate') && 'bg-ink-300',
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-meta text-ink-500">
          <span>{formatBytes(file.size)}</span>
          <span aria-hidden>·</span>
          <span>{file.type || 'application/octet-stream'}</span>
          {note ? (
            <span
              data-testid="upload-row-note"
              className={note.tone === 'critical' ? 'text-posture-critical' : 'text-ink-700'}
            >
              · {note.text}
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <StatePill row={row} />
        {classification ? (
          <div className="flex items-center gap-1.5 text-meta text-ink-500">
            <span className="font-medium text-ink-700">
              {documentClassLabel(classification.corrected_type ?? classification.predicted_type)}
            </span>
            {typeof confidence === 'number' ? <AiConfidenceChip value={confidence} /> : null}
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        {state.kind === 'error' ? (
          <button
            type="button"
            onClick={onRetry}
            aria-label="Retry upload"
            className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300"
          >
            <ArrowUpRight className="h-4 w-4" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={onCancel}
          aria-label={
            state.kind === 'uploading' || state.kind === 'parsing'
              ? 'Cancel upload'
              : 'Remove from list'
          }
          className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** The line under a row's bar that says what happened, when there is one. */
function rowNote(state: RowState): { text: string; tone: 'critical' | 'neutral' } | null {
  switch (state.kind) {
    case 'error':
      return { text: state.message, tone: 'critical' };
    case 'failed':
      return {
        text: state.reason
          ? `Could not read this file: ${state.reason}`
          : 'Could not read this file.',
        tone: 'critical',
      };
    case 'processing':
      return { text: STILL_PROCESSING_MESSAGE, tone: 'neutral' };
    case 'duplicate':
      return { text: duplicateNote(state.existing), tone: 'neutral' };
    default:
      return null;
  }
}

function duplicateNote(existing: ExistingDocument | null): string {
  if (!existing) return 'Already in this engagement — nothing was uploaded.';
  return `Already in this engagement as “${existing.title}”, uploaded ${formatDateTime(
    existing.uploaded_at,
  )} — nothing was uploaded.`;
}

function StatePill({ row }: { row: FileRow }) {
  const s = row.state;
  if (s.kind === 'queued') return <PassiveChip>Queued</PassiveChip>;
  if (s.kind === 'uploading')
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-ai-surface px-2 text-[11px] font-medium text-ai-ink">
        <Spinner className="text-ai-ink" /> Uploading{' '}
        <span className="font-mono">
          {s.totalBytes === 0 ? '0' : Math.floor((s.pushedBytes / s.totalBytes) * 100)}%
        </span>
      </span>
    );
  if (s.kind === 'parsing')
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-posture-at-risk-tint px-2 text-[11px] font-medium text-posture-at-risk">
        <Spinner className="text-posture-at-risk" /> Parsing
      </span>
    );
  if (s.kind === 'parsed')
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-posture-healthy-tint px-2 text-[11px] font-medium text-posture-healthy">
        <Check className="h-3 w-3" aria-hidden="true" /> Parsed
      </span>
    );
  if (s.kind === 'processing')
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-paper-100 px-2 text-[11px] font-medium text-ink-700">
        <Clock className="h-3 w-3" aria-hidden="true" /> Processing
      </span>
    );
  if (s.kind === 'duplicate')
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-paper-100 px-2 text-[11px] font-medium text-ink-700">
        <Copy className="h-3 w-3" aria-hidden="true" /> Already uploaded
      </span>
    );
  // error (the upload failed) / failed (the read failed)
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-posture-critical-tint px-2 text-[11px] font-medium text-posture-critical">
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />{' '}
      {s.kind === 'failed' ? 'Read failed' : 'Error'}
    </span>
  );
}

function PassiveChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-ink-100 bg-paper-0 px-2.5 text-[11px] font-medium text-ink-700">
      {children}
    </span>
  );
}

function StatusBadge({
  tone,
  children,
}: {
  tone: 'healthy' | 'at-risk' | 'ai' | 'neutral' | 'critical';
  children: React.ReactNode;
}) {
  const cls =
    tone === 'healthy'
      ? 'bg-posture-healthy-tint text-posture-healthy'
      : tone === 'at-risk'
        ? 'bg-posture-at-risk-tint text-posture-at-risk'
        : tone === 'ai'
          ? 'bg-ai-surface text-ai-ink'
          : tone === 'critical'
            ? 'bg-posture-critical-tint text-posture-critical'
            : 'bg-paper-100 text-ink-700';
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 text-[11px] font-medium', cls)}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Poll `GET /documents/:id/status` until the read settles or the window
 * ends: `parsed` → Parsed; `failed` → the failure with its reason; a 4xx →
 * that error; still running at the end → the neutral "still processing"
 * row — never a success the server did not report. Network blips and 5xx
 * answers are retried within the window. The row's AbortSignal stops it.
 */
async function pollParseStatus(
  rowId: string,
  documentId: string,
  setState: (rowId: string, next: RowState) => void,
  signal: AbortSignal | undefined,
  qc: ReturnType<typeof useQueryClient>,
  opts: { intervalMs: number; maxAttempts: number },
): Promise<void> {
  setState(rowId, { kind: 'parsing', documentId });
  for (let attempt = 0; attempt < opts.maxAttempts; attempt += 1) {
    if (signal?.aborted) return;
    try {
      const status = await getDocumentStatus(documentId);
      if (signal?.aborted) return;
      const parseStatus = (status.parse_status ?? '').toLowerCase();
      if (parseStatus === 'parsed') {
        const classification = await tryFetchClassification(documentId, qc);
        setState(rowId, { kind: 'parsed', documentId, classification });
        return;
      }
      if (parseStatus === 'failed') {
        setState(rowId, {
          kind: 'failed',
          documentId,
          reason: status.failure_reason?.trim() || null,
        });
        // The failed document is in the Documents list now; refresh it.
        void qc.invalidateQueries({ queryKey: ['engagement-documents'] });
        return;
      }
    } catch (err) {
      if (signal?.aborted) return;
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        setState(rowId, { kind: 'error', message: humanizeError(err) });
        return;
      }
      // Network blip / 5xx — retry within the window.
    }
    if (attempt < opts.maxAttempts - 1) await sleep(opts.intervalMs);
  }
  if (signal?.aborted) return;
  setState(rowId, { kind: 'processing', documentId });
}

/**
 * Best-effort classification fetch — the docs library uses the
 * classifications endpoint to surface predicted/corrected types per
 * doc. We refresh that cache and pluck the new row if present so the
 * upload row in the modal can show the classification chip.
 *
 * Returns null when the doc hasn't surfaced in the classifications
 * list yet (the worker writes asynchronously).
 */
async function tryFetchClassification(
  _documentId: string,
  qc: ReturnType<typeof useQueryClient>,
): Promise<ClassificationItem | null> {
  // Refresh every cached classifications-list query (we don't know
  // engagementId at this level — `engagementId` is closed over in the
  // outer modal — so we use the prefix invalidation pattern). The next
  // refetch surfaces the new row to the library. The `documentId` is
  // unused here for now; reserved for the future cache-pluck path when
  // the engagement key is plumbed through.
  await qc.invalidateQueries({ queryKey: ['documents', 'classifications'] });
  return null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((res) => globalThis.setTimeout(res, ms));
}

function cryptoRandomId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function humanizeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return 'Network error — check connection and retry.';
    // The API's messages are written for people ("Upload incomplete:
    // received 0 of 334,525 bytes — upload the file again").
    return err.message || 'Upload failed.';
  }
  if (err instanceof Error) return err.message;
  return 'Upload failed.';
}
